/**
 * Yakıt Yönetimi Y7 metinleri (ADR 0117 §10, Ek Y7): dönem kilidi ve yeniden açma, etki raporu,
 * dönemin kalite listesi ve dışa aktarım (Excel, PDF, paket). Anahtarlar API değerleriyle (olay,
 * kalite türü ve kodu, hata kodu) eşleşir. MRV/ETS kısmı ertelendi.
 */

export const fuelPeriodEn = {
  lock: {
    title: 'Period Lock',
    locked: 'Locked',
    open: 'Not locked',
    lockedBy: 'Locked on {{at}} by {{by}}.',
    notClosed: 'The month must be closed on board before the office can lock it.',
    shipNote:
      'The office locks a closed month. While a month is locked, no fuel record, consumption or adjustment can be written into it on board.',
    lockAction: 'Lock Period',
    reopenAction: 'Reopen Period',
    lockTitle: 'Lock {{month}}',
    lockBody:
      'The month’s ledger, measurements and consumption are frozen as they are now. New records for this month are rejected on board once the lock arrives.',
    reason: 'Reason',
    reasonOptional: 'Remarks (optional)',
    reasonRequired: 'Reason (required)',
    confirmLock: 'Lock',
    lockedDone: '{{month}} locked.',
    reopenTitle: 'Reopen {{month}}',
    reopenBody:
      'Reopening allows records to be written into the month again. The reason and this impact report are kept permanently with the event.',
    confirmReopen: 'Reopen',
    reopenedDone: '{{month}} reopened.',
    cancel: 'Cancel',
    password: 'Password',
    passwordHint: 'Your company requires the password for this decision.',
    late: '{{count}} record(s) arrived after the lock (written on board before the lock reached the ship).',
    difference: 'Month-end ROB now differs from the locked figure: {{diff}}',
    history: 'Lock history',
    seq: '#',
    event: 'Event',
    at: 'Date (UTC)',
    by: 'By',
    events: { lock: 'Locked', reopen: 'Reopened' },
    noHistory: 'No lock event yet.',
    impact: {
      title: 'Impact report',
      loading: 'Preparing the impact report…',
      records: 'Records in the month (locked → now)',
      recordsValue:
        '{{entries}} ledger lines, {{soundings}} measurements, {{postings}} consumption postings',
      late: 'Late records',
      lateValue:
        '{{entries}} ledger lines, {{soundings}} measurements, {{postings}} consumption postings',
      balances: 'Month-end ROB (locked → now)',
      laterMonths: 'Later months depending on this month',
      none: 'None',
      closedLocked: '{{month}} (closed{{locked}})',
      lockedSuffix: ', locked',
      voyages: 'Voyages with consumption in the month',
      reports: 'Consumption reports',
      quality: 'Open quality findings',
      mrv: 'MRV / ETS results: not applicable (deferred).',
    },
  },
  quality: {
    title: 'Period Quality',
    empty: 'No quality finding in this month.',
    kind: 'Type',
    finding: 'Finding',
    record: 'Record',
    at: 'Time (UTC)',
    kinds: {
      missing_data: 'Missing data',
      estimate: 'Estimate',
      unmatched: 'Unmatched report',
      late: 'Late record',
      unapproved_adjustment: 'Unapproved adjustment',
    },
    codes: {
      NOT_POSTED: 'Report with consumption not submitted',
      CONSUMPTION_GAP: 'Gap in consumption periods (until {{until}})',
      NOT_MEASURED: 'Tank not measured in the month',
      MISSING_PRICE: 'Bunkering without unit price',
      MISSING_DISTANCE: 'Sea passage report without distance',
      METER_MASS_MISMATCH: 'Mass meter reading differs from the entered consumption',
      YEAR_SPLIT_MISSING: 'Year boundary not split',
      ESTIMATED: 'Estimated consumption line',
      UNASSIGNED_VOYAGE: 'Consumption without voyage',
      LATE_ENTRY: 'Ledger line after the lock',
      LATE_SOUNDING: 'Measurement after the lock',
      LATE_POSTING: 'Consumption posting after the lock',
      UNAPPROVED_ADJUSTMENT: 'Adjustment request not approved',
      ADJUSTMENT_NOT_POSTED: 'Approved adjustment not yet posted',
    },
    refs: {
      tank: 'Tank {{name}}',
      period: '—',
    },
  },
  export: {
    title: 'Period Export',
    excel: 'Excel',
    pdf: 'PDF',
    package: 'Package (ZIP)',
    packageHint: 'Excel, PDF and data with SHA-256 hashes in manifest.json',
    done: 'Package ready: {{count}} files, manifest with SHA-256 hashes.',
    reportTitle: 'Fuel Period Report — {{month}}',
    vessel: 'Vessel',
    period: 'Period (UTC)',
    generated: 'Generated',
    closeState: 'Month close',
    lockState: 'Period lock',
    closedOn: 'Closed {{at}}',
    notClosed: 'Not closed',
    lockedOn: 'Locked {{at}} (#{{seq}})',
    notLocked: 'Not locked',
    footer: 'Felixmate · fuel period export (ledger is immutable; MRV/ETS deferred)',
    sheets: {
      summary: 'Summary',
      ledger: 'Fuel Ledger',
      bunkerings: 'Bunkerings',
      operations: 'Transfers',
      soundings: 'Measurements',
      adjustments: 'Adjustments',
      consumption: 'Consumption',
      quality: 'Quality',
      lock: 'Lock Events',
    },
    columns: {
      fuel: 'Fuel',
      tank: 'Tank',
      opening: 'Opening ROB (MT)',
      closing: 'Closing ROB (MT)',
      at: 'Date / Time (UTC)',
      kind: 'Type',
      mass: 'MT',
      source: 'Source',
      sourceNo: 'Source No',
      revision: 'Rev.',
      recordNo: 'Record No',
      bdnNo: 'BDN No',
      supplier: 'Supplier',
      port: 'Port',
      status: 'Status',
      bdnFile: 'BDN file',
      sha256: 'SHA-256',
      tanks: 'Tanks',
      soundingNo: 'Measurement No',
      method: 'Method',
      conversion: 'Conversion',
      description: 'Description',
      reportNo: 'Report No',
      report: 'Report',
      voyage: 'Voyage',
      periodStart: 'Period Start',
      periodEnd: 'Period End',
      operation: 'Operation',
      finding: 'Finding',
      record: 'Record',
      event: 'Event',
      by: 'By',
      reason: 'Reason',
    },
  },
};

export const fuelPeriodTr: typeof fuelPeriodEn = {
  lock: {
    title: 'Dönem Kilidi',
    locked: 'Kilitli',
    open: 'Kilitli değil',
    lockedBy: '{{at}} tarihinde {{by}} kilitledi.',
    notClosed: 'Ofisin kilitleyebilmesi için ay önce gemide kapatılmalı.',
    shipNote:
      'Kapanmış ayı ofis kilitler. Ay kilitliyken gemide o aya yakıt kaydı, tüketim ya da düzeltme yazılamaz.',
    lockAction: 'Dönemi Kilitle',
    reopenAction: 'Dönemi Yeniden Aç',
    lockTitle: '{{month}} dönemini kilitle',
    lockBody:
      'Ayın defteri, ölçümleri ve tüketimi bugünkü haliyle dondurulur. Kilit gemiye ulaşınca bu aya yeni kayıt gemide reddedilir.',
    reason: 'Gerekçe',
    reasonOptional: 'Açıklama (isteğe bağlı)',
    reasonRequired: 'Gerekçe (zorunlu)',
    confirmLock: 'Kilitle',
    lockedDone: '{{month}} kilitlendi.',
    reopenTitle: '{{month}} dönemini yeniden aç',
    reopenBody:
      'Yeniden açınca aya yeniden kayıt yazılabilir. Gerekçe ve bu etki raporu olayla birlikte kalıcı olarak saklanır.',
    confirmReopen: 'Yeniden Aç',
    reopenedDone: '{{month}} yeniden açıldı.',
    cancel: 'Vazgeç',
    password: 'Parola',
    passwordHint: 'Şirketiniz bu karar için parola istiyor.',
    late: 'Kilitten sonra {{count}} kayıt geldi (kilit gemiye ulaşmadan gemide yazılmış).',
    difference: 'Ay sonu ROB kilitteki değerden farklı: {{diff}}',
    history: 'Kilit geçmişi',
    seq: '#',
    event: 'Olay',
    at: 'Tarih (UTC)',
    by: 'Yapan',
    events: { lock: 'Kilitlendi', reopen: 'Yeniden açıldı' },
    noHistory: 'Henüz kilit olayı yok.',
    impact: {
      title: 'Etki raporu',
      loading: 'Etki raporu hazırlanıyor…',
      records: 'Aydaki kayıtlar (kilit → bugün)',
      recordsValue: '{{entries}} defter satırı, {{soundings}} ölçüm, {{postings}} tüketim başlığı',
      late: 'Geç kayıtlar',
      lateValue: '{{entries}} defter satırı, {{soundings}} ölçüm, {{postings}} tüketim başlığı',
      balances: 'Ay sonu ROB (kilit → bugün)',
      laterMonths: 'Bu aya dayanan sonraki aylar',
      none: 'Yok',
      closedLocked: '{{month}} (kapalı{{locked}})',
      lockedSuffix: ', kilitli',
      voyages: 'Ayda tüketimi olan seferler',
      reports: 'Tüketim raporları',
      quality: 'Açık kalite bulguları',
      mrv: 'MRV / ETS sonuçları: uygulanmıyor (ertelendi).',
    },
  },
  quality: {
    title: 'Dönem Kalitesi',
    empty: 'Bu ayda kalite bulgusu yok.',
    kind: 'Tür',
    finding: 'Bulgu',
    record: 'Kayıt',
    at: 'Zaman (UTC)',
    kinds: {
      missing_data: 'Eksik veri',
      estimate: 'Tahmin',
      unmatched: 'Eşleşmemiş rapor',
      late: 'Geç kayıt',
      unapproved_adjustment: 'Onaysız düzeltme',
    },
    codes: {
      NOT_POSTED: 'Tüketimli rapor gönderilmemiş',
      CONSUMPTION_GAP: 'Tüketim aralıklarında boşluk ({{until}} tarihine kadar)',
      NOT_MEASURED: 'Tank ay içinde ölçülmemiş',
      MISSING_PRICE: 'Birim fiyatı olmayan alım',
      MISSING_DISTANCE: 'Mesafesi olmayan seyir raporu',
      METER_MASS_MISMATCH: 'Kütle sayacı girilen tüketimle uyuşmuyor',
      YEAR_SPLIT_MISSING: 'Yıl sınırı ayrılmamış',
      ESTIMATED: 'Tahmini tüketim satırı',
      UNASSIGNED_VOYAGE: 'Seferi olmayan tüketim',
      LATE_ENTRY: 'Kilitten sonra defter satırı',
      LATE_SOUNDING: 'Kilitten sonra ölçüm',
      LATE_POSTING: 'Kilitten sonra tüketim başlığı',
      UNAPPROVED_ADJUSTMENT: 'Onaylanmamış düzeltme talebi',
      ADJUSTMENT_NOT_POSTED: 'Onaylı düzeltme henüz deftere işlenmedi',
    },
    refs: {
      tank: '{{name}} tankı',
      period: '—',
    },
  },
  export: {
    title: 'Dönem Dışa Aktarımı',
    excel: 'Excel',
    pdf: 'PDF',
    package: 'Paket (ZIP)',
    packageHint: "Excel, PDF ve veri; SHA-256 hash'leri manifest.json'da",
    done: "Paket hazır: {{count}} dosya, SHA-256 hash'leri manifestte.",
    reportTitle: 'Yakıt Dönem Raporu — {{month}}',
    vessel: 'Gemi',
    period: 'Dönem (UTC)',
    generated: 'Oluşturulma',
    closeState: 'Ay kapanışı',
    lockState: 'Dönem kilidi',
    closedOn: '{{at}} kapandı',
    notClosed: 'Kapanmadı',
    lockedOn: '{{at}} kilitlendi (#{{seq}})',
    notLocked: 'Kilitli değil',
    footer: 'Felixmate · yakıt dönemi dışa aktarımı (defter değiştirilemez; MRV/ETS ertelendi)',
    sheets: {
      summary: 'Özet',
      ledger: 'Yakıt Defteri',
      bunkerings: 'Alımlar',
      operations: 'Transferler',
      soundings: 'Ölçümler',
      adjustments: 'Düzeltmeler',
      consumption: 'Tüketim',
      quality: 'Kalite',
      lock: 'Kilit Olayları',
    },
    columns: {
      fuel: 'Yakıt',
      tank: 'Tank',
      opening: 'Açılış ROB (MT)',
      closing: 'Kapanış ROB (MT)',
      at: 'Tarih / Saat (UTC)',
      kind: 'Tür',
      mass: 'MT',
      source: 'Kaynak',
      sourceNo: 'Kaynak No',
      revision: 'Rev.',
      recordNo: 'Kayıt No',
      bdnNo: 'BDN No',
      supplier: 'Tedarikçi',
      port: 'Liman',
      status: 'Durum',
      bdnFile: 'BDN dosyası',
      sha256: 'SHA-256',
      tanks: 'Tanklar',
      soundingNo: 'Ölçüm No',
      method: 'Yöntem',
      conversion: 'Dönüşüm',
      description: 'Açıklama',
      reportNo: 'Rapor No',
      report: 'Rapor',
      voyage: 'Sefer',
      periodStart: 'Dönem Başı',
      periodEnd: 'Dönem Sonu',
      operation: 'Operasyon',
      finding: 'Bulgu',
      record: 'Kayıt',
      event: 'Olay',
      by: 'Yapan',
      reason: 'Gerekçe',
    },
  },
};

export const fuelPeriodErrorsEn = {
  PERIOD_LOCKED: 'This month is locked by the office: no record can be written into it.',
  PERIOD_ALREADY_LOCKED: 'This month is already locked.',
  PERIOD_NOT_LOCKED: 'This month is not locked.',
  MONTH_NOT_CLOSED: 'The month has not been closed on board yet.',
  REOPEN_REASON_REQUIRED: 'Enter the reason for reopening.',
  IMPACT_REPORT_REQUIRED: 'The impact report is required to reopen.',
  IMPACT_REPORT_STALE: 'The data changed: review the updated impact report and confirm again.',
};

export const fuelPeriodErrorsTr: typeof fuelPeriodErrorsEn = {
  PERIOD_LOCKED: 'Bu ay ofis tarafından kilitli: aya kayıt yazılamaz.',
  PERIOD_ALREADY_LOCKED: 'Bu ay zaten kilitli.',
  PERIOD_NOT_LOCKED: 'Bu ay kilitli değil.',
  MONTH_NOT_CLOSED: 'Ay henüz gemide kapatılmadı.',
  REOPEN_REASON_REQUIRED: 'Yeniden açma gerekçesini yazın.',
  IMPACT_REPORT_REQUIRED: 'Yeniden açmak için etki raporu gerekli.',
  IMPACT_REPORT_STALE: 'Veri değişti: güncel etki raporunu inceleyip yeniden onaylayın.',
};
